import { redactLine, redactText } from '../bootstrap/redact.js';
import { REDACTION_RULES_VERSION } from '../bootstrap/redaction.js';
import { distinctFailures } from '../runs/failure-extract.js';
/**
 * A diagnostic bundle: everything support would otherwise gather by hand about
 * one instance, in one file (IM-54).
 *
 * ## What it is for
 *
 * Somebody says an instance is broken. Today that is a support engineer
 * opening four screens, a CloudWatch console in the customer's account and a
 * CloudFormation one, and pasting what they find into a ticket — by which point
 * whoever picks the ticket up has a partial copy, taken at different moments,
 * of things they cannot themselves go and look at. The bundle is that copy
 * taken once, by the control plane, through the diagnostics role alone.
 *
 * ## Why it can be handed to anyone at Qrvey
 *
 * It is a file with no access attached. Nothing in it lets the reader reach
 * the account: no external id, no assumed-role credentials, no registry key,
 * no run secret, no pre-signed anything. Reading it grants nothing, so sending
 * it to engineering needs no grant in the customer's account and no role here.
 * `bundle.test.ts` and the handler test scan a built bundle for secret-shaped
 * content rather than trusting this paragraph.
 *
 * ## Why every free-text field is redacted here
 *
 * Run logs were redacted in the customer's account before they were written.
 * A stack event's status reason, a cluster health message and a run's failure
 * detail were not — they are the cloud's own words, read back through an API —
 * and the bundle is exactly the artifact that travels furthest. So each passes
 * through `redactText`, which executes the same rules the customer's redactor
 * does. Log lines go through it a **second** time, deliberately: the rules a
 * customer's dispatcher runs are whatever they last deployed (IM-84), and a
 * file that leaves the product should be masked with the rules this build
 * ships, not the ones a stale stack carried.
 *
 * ## Unknown is not done
 *
 * Every section is three-valued. A read that failed is `unreadable`, with the
 * reason; a section with nothing to say is `not-applicable`, with why. Neither
 * is ever silently absent, because a bundle missing its stack section looks
 * exactly like an account with no stacks — and that is the wrong conclusion
 * for whoever reads it next week.
 *
 * Pure: the reads happen in the handler and the adapters, and this only shapes
 * and bounds what they found. Free of `node:` imports, because `shared` is
 * imported into a browser.
 */
export const DIAGNOSTIC_BUNDLE_FORMAT = 'qrvey-diagnostic-bundle';
/** Bumped when a reader of an older bundle would misread a newer one. */
export const DIAGNOSTIC_BUNDLE_VERSION = 1;
/**
 * The limits, and why each is where it is.
 *
 * The total cap is what the acceptance criterion is about: a bundle has to fit
 * in a ticket and an email. The per-part caps keep the ordinary bundle far
 * under it, so the total only ever bites on a pathological account — and when
 * it does, the section says what was cut.
 */
export const DIAGNOSTIC_LIMITS = {
    /** 5 MB, the ceiling most ticket and mail systems accept for an attachment. */
    maxBytes: 5 * 1024 * 1024,
    /** Per log. The failure is almost always at the end, which is what is kept. */
    maxLogLines: 2_000,
    /** A single line longer than this is a JSON blob or a base64 run, not prose. */
    maxLineChars: 4_000,
    /** Newest first. An instance with more history than this has a longer story than a ticket. */
    maxRuns: 50,
    /** Per stack, newest first — the failure that matters is the most recent. */
    maxStackEvents: 25,
};
export const DIAGNOSTIC_SECTIONS = [
    'deployment',
    'runs',
    'onboarding',
    'stacks',
    'cluster',
    'kubernetes',
    'latestRunLog',
    'failedRunLog',
];
const REDACTION_NOTE = 'Run log lines were masked in the customer’s own account before they were written, and again here. ' +
    'Stack event reasons, cluster health messages and run failure details were masked here, with the same rules. ' +
    'What runs inside the cluster was reduced to named fields — no environment values, arguments or annotations — and its messages masked the same way. ' +
    'No credential, external id, registry key or run secret is included; this file grants no access to the account.';
/** The same shaping every gathered section goes through. */
const sectionOf = (gathered, shape) => {
    if (gathered.state === 'unreadable') {
        return { state: 'unreadable', reason: redactText(gathered.reason) };
    }
    if (gathered.state === 'not-applicable')
        return gathered;
    const { data, truncated } = shape(gathered.value);
    return { state: 'collected', data, ...(truncated === undefined ? {} : { truncated }) };
};
const clip = (text) => text.length <= DIAGNOSTIC_LIMITS.maxLineChars
    ? text
    : `${text.slice(0, DIAGNOSTIC_LIMITS.maxLineChars)}… [cut at ${String(DIAGNOSTIC_LIMITS.maxLineChars)} characters]`;
/** Free text from outside this control plane: clipped, then masked. */
const scrub = (text) => redactText(clip(text));
/**
 * The instance as this control plane records it.
 *
 * An allow list, not a spread: `DeploymentSummary` carries `variables` — what
 * the instance was deployed with, minus the secret-shaped ones — and those
 * include an administrator's email and whatever a future chart adds under a
 * name nobody has heard of. None of it diagnoses anything the status and the
 * runs do not, so none of it travels.
 */
const deploymentData = (d) => ({
    deploymentId: d.deploymentId,
    accountKey: d.accountKey,
    status: d.status,
    ...(d.region === undefined ? {} : { region: d.region }),
    ...(d.chartVersion === undefined ? {} : { chartVersion: d.chartVersion }),
    ...(d.installer === undefined ? {} : { installerImage: d.installer.image }),
    ...(d.liveSince === undefined ? {} : { liveSince: d.liveSince }),
    ...(d.stateBucket === undefined ? {} : { stateBucket: d.stateBucket }),
    ...(d.stateKey === undefined ? {} : { stateKey: d.stateKey }),
    ...(d.adopted === undefined ? {} : { adopted: d.adopted }),
    ...(d.hidden === undefined ? {} : { hidden: { at: d.hidden.at, by: d.hidden.by } }),
    ...(d.destroyedOutside === undefined
        ? {}
        : {
            destroyedOutside: {
                at: d.destroyedOutside.at,
                by: d.destroyedOutside.by,
                scannedAt: d.destroyedOutside.scannedAt,
            },
        }),
    ...(d.scanSaysGone === undefined
        ? {}
        : {
            scanSaysGone: { scannedAt: d.scanSaysGone.scannedAt, evidence: d.scanSaysGone.evidence },
        }),
    ...(d.lifetime === undefined
        ? {}
        : { lifetime: { expiresAt: d.lifetime.expiresAt, at: d.lifetime.at, by: d.lifetime.by } }),
    ...(d.outputs === undefined ? {} : { outputs: withheldStaysWithheld(d.outputs) }),
});
/**
 * Stored outputs, with a present value re-masked.
 *
 * A sensitive field is never stored at all (IM-40), so there is nothing to
 * withhold here that storage did not already. A present value is an address
 * or a version; it goes through the redactor anyway, because a rule that
 * stopped matching in the customer's account would otherwise have its miss
 * carried into a file that leaves the product.
 */
const withheldStaysWithheld = (outputs) => ({
    fromRunId: outputs.fromRunId,
    readAt: outputs.readAt,
    values: Object.fromEntries(Object.entries(outputs.values).map(([name, value]) => [
        name,
        value.state === 'present' ? { state: 'present', value: redactLine(value.value) } : value,
    ])),
});
const runEntry = (run) => ({
    runId: run.runId,
    command: run.command,
    state: run.state,
    requestedBy: run.requestedBy,
    requestedAt: run.requestedAt,
    ...(run.startedAt === undefined ? {} : { startedAt: run.startedAt }),
    ...(run.finishedAt === undefined ? {} : { finishedAt: run.finishedAt }),
    ...(run.region === undefined ? {} : { region: run.region }),
    ...(run.chartVersion === undefined ? {} : { chartVersion: run.chartVersion }),
    ...(run.installerImage === undefined ? {} : { installerImage: run.installerImage }),
    ...(run.reference === undefined ? {} : { reference: run.reference }),
    ...(run.failure === undefined
        ? {}
        : { failure: { reason: run.failure.reason, detail: scrub(run.failure.detail) } }),
});
const stackData = (stacks) => {
    let cut = false;
    const data = stacks.map((stack) => {
        if (stack.state !== 'found') {
            return stack.state === 'unreadable' ? { ...stack, reason: scrub(stack.reason) } : stack;
        }
        const events = stack.events;
        if (events !== undefined && events.length > DIAGNOSTIC_LIMITS.maxStackEvents)
            cut = true;
        return {
            ...stack,
            ...(stack.statusReason === undefined ? {} : { statusReason: scrub(stack.statusReason) }),
            ...(stack.eventsUnreadable === undefined
                ? {}
                : { eventsUnreadable: scrub(stack.eventsUnreadable) }),
            ...(events === undefined
                ? {}
                : {
                    events: events.slice(0, DIAGNOSTIC_LIMITS.maxStackEvents).map((event) => ({
                        ...event,
                        ...(event.reason === undefined ? {} : { reason: scrub(event.reason) }),
                    })),
                }),
        };
    });
    return cut
        ? {
            data,
            truncated: `Only the newest ${String(DIAGNOSTIC_LIMITS.maxStackEvents)} events of each stack are included.`,
        }
        : { data };
};
const clusterData = (cluster) => {
    if (cluster.state === 'found') {
        return {
            data: {
                ...cluster,
                issues: cluster.issues.map((i) => ({ code: i.code, message: scrub(i.message) })),
            },
        };
    }
    if (cluster.state === 'unreadable')
        return { data: { ...cluster, reason: scrub(cluster.reason) } };
    return { data: cluster };
};
const kubernetesData = (state) => {
    const cut = Object.values(state.kinds).some((k) => k.state === 'read' && k.truncated !== undefined);
    return cut
        ? { data: state, truncated: 'Some kinds were cut to fit; each says how.' }
        : { data: state };
};
const logData = (input) => {
    const kept = input.lines.slice(-DIAGNOSTIC_LIMITS.maxLogLines);
    const lines = kept.map((line) => ({ at: line.at, message: scrub(line.message) }));
    return {
        data: {
            runId: input.run.runId,
            command: input.run.command,
            state: input.run.state,
            requestedAt: input.run.requestedAt,
            failures: distinctFailures(lines.map((l) => l.message)),
            lines,
        },
        ...(kept.length < input.lines.length
            ? {
                truncated: `Only the last ${String(DIAGNOSTIC_LIMITS.maxLogLines)} lines are included; the whole log is in the customer’s own log group.`,
            }
            : {}),
    };
};
/**
 * UTF-8 bytes, which is what an attachment limit counts.
 *
 * Counted by hand rather than with `TextEncoder`, which `shared`'s build
 * config does not type: it carries neither the DOM nor Node's globals, so the
 * browser and the Lambda both compile it.
 */
export const utf8Length = (text) => {
    let bytes = 0;
    for (const char of text) {
        const code = char.codePointAt(0) ?? 0;
        bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
    }
    return bytes;
};
export const bundleBytes = (bundle) => utf8Length(JSON.stringify(bundle));
const linesIn = (section) => section.state === 'collected' ? section.data.lines.length : 0;
/** Halves one log section, keeping its end — where the failure is. */
const halveLog = (bundle, key) => {
    const section = bundle.sections[key];
    if (section.state !== 'collected')
        return bundle;
    const keep = Math.floor(section.data.lines.length / 2);
    return {
        ...bundle,
        sections: {
            ...bundle.sections,
            [key]: {
                ...section,
                data: {
                    ...section.data,
                    lines: section.data.lines.slice(section.data.lines.length - keep),
                },
                truncated: `Cut to the last ${String(keep)} lines to keep the bundle under ${String(Math.round(bundle.limits.maxBytes / 1024 / 1024))} MB; the whole log is in the customer’s own log group.`,
            },
        },
    };
};
/**
 * Builds the bundle and holds it under the size cap.
 *
 * The logs are what grows, so they are what is cut — the larger one halved,
 * from the front, until the whole fits. The failure is at the end of a log
 * and stays. If the logs are gone and it still does not fit, the answer is
 * the smallest honest one rather than a file nobody can attach: both log
 * sections say so. `maxBytes` is a parameter only so a test can reach the
 * branch without building five megabytes.
 */
export const buildDiagnosticBundle = (input, maxBytes = DIAGNOSTIC_LIMITS.maxBytes) => {
    const history = input.deployment.history;
    const runs = history.slice(0, DIAGNOSTIC_LIMITS.maxRuns).map(runEntry);
    let bundle = {
        format: DIAGNOSTIC_BUNDLE_FORMAT,
        formatVersion: DIAGNOSTIC_BUNDLE_VERSION,
        collectedAt: input.collectedAt,
        collectedBy: input.collectedBy,
        tenantId: input.tenantId,
        accountKey: input.accountKey,
        deploymentId: input.deploymentId,
        region: input.region,
        redaction: { rulesVersion: REDACTION_RULES_VERSION, note: REDACTION_NOTE },
        limits: { ...DIAGNOSTIC_LIMITS, maxBytes },
        sections: {
            deployment: { state: 'collected', data: deploymentData(input.deployment) },
            runs: history.length === 0
                ? {
                    state: 'not-applicable',
                    reason: input.deployment.adopted === undefined
                        ? 'Nothing has been run against this instance from this control plane.'
                        : 'This instance was adopted and nothing has been run against it from here since.',
                }
                : {
                    state: 'collected',
                    data: runs,
                    ...(history.length > runs.length
                        ? {
                            truncated: `The newest ${String(runs.length)} of ${String(history.length)} runs are included.`,
                        }
                        : {}),
                },
            onboarding: sectionOf(input.onboarding, (value) => ({ data: value })),
            stacks: sectionOf(input.stacks, stackData),
            cluster: sectionOf(input.cluster, clusterData),
            kubernetes: sectionOf(input.kubernetes, kubernetesData),
            latestRunLog: sectionOf(input.latestRunLog, logData),
            failedRunLog: sectionOf(input.failedRunLog, logData),
        },
    };
    while (bundleBytes(bundle) > maxBytes) {
        const latest = linesIn(bundle.sections.latestRunLog);
        const failed = linesIn(bundle.sections.failedRunLog);
        if (latest === 0 && failed === 0)
            break;
        bundle = halveLog(bundle, latest >= failed ? 'latestRunLog' : 'failedRunLog');
    }
    return bundle;
};
export const summarizeBundle = (bundle) => {
    const of = (state) => DIAGNOSTIC_SECTIONS.filter((id) => bundle.sections[id].state === state);
    return {
        collected: of('collected'),
        unreadable: of('unreadable'),
        notApplicable: of('not-applicable'),
        truncated: DIAGNOSTIC_SECTIONS.filter((id) => {
            const section = bundle.sections[id];
            return section.state === 'collected' && section.truncated !== undefined;
        }),
    };
};
/**
 * The file name the console saves it under.
 *
 * The deployment and the moment, so two bundles in one ticket are not both
 * `bundle.json` and the later one is recognisable. Colons are replaced because
 * Windows refuses them in a file name.
 */
export const diagnosticBundleFilename = (deploymentId, collectedAt) => `qrvey-diagnostics-${deploymentId}-${collectedAt.replace(/\.\d+Z$/, 'Z').replace(/:/g, '-')}.json`;
//# sourceMappingURL=bundle.js.map